import { Injectable } from '@angular/core';
import { Observable, defer, throwError, timer } from 'rxjs';
import { catchError, map, mergeMap, retryWhen } from 'rxjs/operators';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import { WindowPayloadBuilderService } from './window-payload-builder.service';
import { StorageService } from './storage.service';
import { environment } from 'src/environments/environment';
import * as Constants from '../utils/Constants';

@Injectable({ providedIn: 'root' })
export class SpacerApiService {

  private readonly RETRY_COUNT   = 2;
  private readonly BASE_DELAY_MS = 200;
  private readonly psiUrl = environment.caluwin_api.base_url + '/api/Spacer/psi';

  constructor(
    private payloadBuilder: WindowPayloadBuilderService,
    private storageService: StorageService
  ) {}

  // ─────────────────────────────────────────────
  // replace spacers in master data
  // ─────────────────────────────────────────────
  fetchAndUpdateSpacers(): Observable<any[]> {
    return defer(async () => {
      const { window1 } = await this.payloadBuilder.buildWindowPayloads();
      return CapacitorHttp.post({
        url:     this.psiUrl,
        headers: this.getHeaders(),
        data:    window1
      });
    }).pipe(
      map((response: HttpResponse) => {
        if (response.status < 200 || response.status >= 300) throw response;

        const updatedSpacers: any[] = response.data;

        // ── replace spacers in MASTER_DATA_V1 ──
        const rawMasterData = this.storageService.get(Constants.STORAGE_KEY);
        if (rawMasterData) {
          const masterData = JSON.parse(rawMasterData);
          masterData.spacers = updatedSpacers;
          this.storageService.set(Constants.STORAGE_KEY, masterData);
          console.log('Master data spacers replaced with psi values');
        }

        return updatedSpacers;
      }),
      retryWhen(this.retryWithBackoff()),
      catchError(err => throwError(() => this.handleFinalError(err)))
    );
  }

  // ─────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────
  private getHeaders(): Record<string, string> {
    return { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  }

  private retryWithBackoff() {
    return (errors: Observable<any>) =>
      errors.pipe(
        mergeMap((error, retryIndex) => {
          const attempt = retryIndex + 1;
          if (attempt > this.RETRY_COUNT || !this.isRetryableError(error?.status)) {
            return throwError(() => error);
          }
          return timer(Math.pow(2, attempt) * this.BASE_DELAY_MS);
        })
      );
  }

  private isRetryableError(status?: number): boolean {
    return [0, 408, 429, 500, 502, 503, 504].includes(status ?? 0);
  }

  private handleFinalError(error: any): Error {
    if (typeof error?.status === 'number') {
      console.error('Spacer API Error:', error.status, error.data);
      return new Error(error.data?.message || `HTTP Error ${error.status}`);
    }
    console.error('Network Error:', error);
    return new Error('Network error. Please check your connection.');
  }
}
